import 'moment/locale/fr';
import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import {
  FaCaretRight,
  FaPlus,
  FaRegCircleXmark,
  FaRegFloppyDisk,
  FaRegPenToSquare,
  FaRegTrashCan,
} from 'react-icons/fa6';

import axios from 'axios';
import InputGroup from 'react-bootstrap/esm/InputGroup';
import { IAgenda, IMeeting } from '../../../../../types';
import { IconWrapper } from '../../../../IconWrapper/IconWrapper';
import styles from './Agenda.module.scss';

interface IGlobalProps {
  meeting: IMeeting;
  onChange: () => void;
}

const Agenda = ({ meeting, onChange }: IGlobalProps) => {
  const [editMode, setEditMode] = useState<boolean>(false);
  const [agenda, setAgenda] = useState<IAgenda[]>([]);

  useEffect(() => {
    initForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting]);

  const initForm = () => {
    setAgenda(meeting.agenda);
  };

  const handleSave = () => {
    setEditMode(false);

    axios
      .put(`/api/meeting/${meeting._id}/agenda`, agenda)
      .then(() => {
        onChange();
      })
      .catch(() => initForm());
  };

  const handleCancel = () => {
    setEditMode(false);
    initForm();
  };

  return (
    <Card className={styles.Agenda}>
      <Card.Header className="d-flex justify-content-between">
        <Col>Agenda</Col>
        <Col className="d-flex justify-content-end gap-2">
          {!editMode && (
            <Button size="sm" variant="warning" title="Edit" onClick={() => setEditMode(true)}>
              <IconWrapper icon={FaRegPenToSquare} />
            </Button>
          )}
          {editMode && (
            <ButtonGroup aria-label="Actions">
              <Button size="sm" variant="primary" onClick={handleSave} title="Save">
                <IconWrapper icon={FaRegFloppyDisk} />
              </Button>
              <Button size="sm" variant="secondary" onClick={handleCancel} title="Cancel">
                <IconWrapper icon={FaRegCircleXmark} />
              </Button>
            </ButtonGroup>
          )}
        </Col>
      </Card.Header>
      <Card.Body>
        {agenda.map((a, id) => (
          <React.Fragment key={id}>
            {id > 0 && <hr />}
            <Row className="gap-1 mb-2">
              <Col sm={3} className="pe-0">
                <Form.Control
                  name="time"
                  size="sm"
                  value={a.time}
                  onChange={(e) =>
                    setAgenda((prev) => {
                      const newAgenda = [...prev];
                      newAgenda[id].time = e.target.value;
                      return newAgenda;
                    })
                  }
                  type="time"
                  className="text-center fw-bold"
                  readOnly={!editMode}
                />
              </Col>
              <Col className="ps-0">
                {!editMode && (
                  <Form.Control
                    name="title"
                    size="sm"
                    value={a.title}
                    onChange={(e) =>
                      setAgenda((prev) => {
                        const newAgenda = [...prev];
                        newAgenda[id].title = e.target.value;
                        return newAgenda;
                      })
                    }
                    className="fw-bold"
                    readOnly={!editMode}
                  />
                )}
                {editMode && (
                  <InputGroup>
                    <Form.Control
                      name="title"
                      size="sm"
                      value={a.title}
                      onChange={(e) =>
                        setAgenda((prev) => {
                          const newAgenda = [...prev];
                          newAgenda[id].title = e.target.value;
                          return newAgenda;
                        })
                      }
                      className="fw-bold"
                      readOnly={!editMode}
                    />
                    <Button
                      size="sm"
                      variant="danger"
                      title="Delete"
                      onClick={() => {
                        setAgenda((prev) => {
                          const newAgenda = [...prev];
                          newAgenda.splice(id, 1);
                          return newAgenda;
                        });
                      }}
                    >
                      <IconWrapper icon={FaRegTrashCan} />
                    </Button>
                  </InputGroup>
                )}
              </Col>
            </Row>
            {a.topics.map((t, tid) => (
              <React.Fragment key={tid}>
                <Row className={`${styles.Topic} mb-2`}>
                  <Col sm={1} className="pe-0">
                    <IconWrapper icon={FaCaretRight} />
                  </Col>
                  <Col className="ps-0">
                    {!editMode && (
                      <Form.Control
                        name="title"
                        size="sm"
                        value={t}
                        as="textarea"
                        rows={t.split(/\n/).length}
                        readOnly
                      />
                    )}
                    {editMode && (
                      <InputGroup>
                        <Form.Control
                          name="title"
                          size="sm"
                          value={t}
                          as="textarea"
                          rows={t.split(/\n/).length}
                          onChange={(e) =>
                            setAgenda((prev) => {
                              const newAgenda = [...prev];
                              newAgenda[id].topics[tid] = e.target.value;
                              return newAgenda;
                            })
                          }
                        />
                        <Button
                          size="sm"
                          variant="outline-danger"
                          title="Delete"
                          onClick={() => {
                            setAgenda((prev) => {
                              const newAgenda = [...prev];
                              newAgenda[id].topics.splice(tid, 1);
                              return newAgenda;
                            });
                          }}
                        >
                          <IconWrapper icon={FaRegTrashCan} />
                        </Button>
                      </InputGroup>
                    )}
                  </Col>
                </Row>
              </React.Fragment>
            ))}
            {editMode && (
              <Row className={`${styles.Topic} mb-2`}>
                <Col sm={1} className="pe-0">
                  <IconWrapper icon={FaCaretRight} />
                </Col>
                <Col className="ps-0">
                  <Button
                    size="sm"
                    className="w-100"
                    variant="outline-primary"
                    onClick={() => {
                      setAgenda((prev) => {
                        console.log(prev);
                        const newAgenda = [...prev];
                        newAgenda[id].topics.push('');
                        return newAgenda;
                      });
                    }}
                  >
                    <IconWrapper icon={FaPlus} /> Add topic
                  </Button>
                </Col>
              </Row>
            )}
          </React.Fragment>
        ))}
        {editMode && (
          <>
            <hr />
            <Row className={`${styles.Topic} mb-2`}>
              <Col>
                <Button
                  size="sm"
                  className="w-100"
                  variant="primary"
                  onClick={() => {
                    setAgenda((prev) => {
                      console.log(prev);
                      const newAgenda = [...prev];
                      newAgenda.push({ time: '', title: '', topics: [] });
                      return newAgenda;
                    });
                  }}
                >
                  <IconWrapper icon={FaPlus} /> Add agenda
                </Button>
              </Col>
            </Row>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default Agenda;
